import { Header4 } from 'structure/home/components/Typography';
import { Header6 } from 'components/Typography';
import {
  getNextTierBySrep,
  getTierBySrep,
} from 'structure/launchpad/utils/tiers.utils';
import { useWalletSREP } from 'structure/staking/hooks/useStakingAPI';
import { parseSREP } from 'structure/staking/utils/parseSREP';
import { useTranslation } from 'react-i18next';
import { useConnection } from 'wagmi';
import Link from 'next/link';
import Image from 'next/image';
import { ProgressBar } from 'baseui/progress-bar';
import { Tooltip } from '@dapp/multi-lib/ui/V2Tooltip';
import { Typography } from '@dapp/multi-lib/sdaoui/Typography';
import { getStaticAssetUrl } from 'utils/url';
import { FormateNumberWithCommas } from 'utils/constants/formate-number-with-commas.const';
import { Spinner } from 'baseui/spinner';
import { isAddress } from 'viem';

export function UserReputationInfo() {
  const { t } = useTranslation('common');
  const account = useConnection();
  const { data = { srep: '0' }, isFetched } = useWalletSREP({
    walletAddress: account.address as `0x${string}`,
    query: {
      enabled: account.isConnected && isAddress(account.address ?? ''),
    },
  });

  const calcProgress = (srep: number, min: number, max: number) => {
    const onePercent = (max - min) / 100;
    const value = srep - min;
    const percentage = Math.max(Math.round(value / onePercent), 0);
    return Math.min(percentage, 100);
  };

  const srep = parseSREP(data.srep || '0').toNumber();
  const tier = getTierBySrep(srep);
  const nextTier = getNextTierBySrep(srep);
  const progress = calcProgress(srep, tier?.srep, nextTier?.srep ?? tier?.srep);
  const nextTierDiff = (nextTier?.srep ?? 0) - srep;

  return (
    <div className='h-fit w-1/3 justify-end border-l-4 border-green bg-backgroundDark-tertiary py-5 max-xl:w-1/2 max-lg:w-full'>
      <div className='flex w-full flex-col gap-4 px-4 max-lg:p-0 '>
        <div className='flex flex-col gap-2 ps-2  max-lg:p-4'>
          <Header4 className='mb-1.5 text-xl text-white-100'>
            {t('TIER_LEVEL')}: {tier.name ? tier.name : t('NO_TIER')}
          </Header4>
          <div className='flex flex-col gap-y-2.5'>
            <div className='flex items-center gap-x-3'>
              <Header6 className='text-sm font-bold text-mono-100'>
                {t('SFIREP')} :
              </Header6>
              {isFetched ? (
                <Header6 className='text-sm font-normal text-gray-100'>
                  {FormateNumberWithCommas(srep?.toString())} {t('SFIREP')}
                </Header6>
              ) : (
                <Spinner $size='14px' $borderWidth='3px' />
              )}
            </div>
            <div className='relative w-full'>
              <ProgressBar
                value={progress}
                overrides={{
                  BarContainer: {
                    style: () => ({
                      all: 'unset',
                      marginLeft: 0,
                    }),
                  },
                  Bar: {
                    style: () => ({
                      backgroundColor: 'black',
                      height: '10px',
                      border: '0.1px solid #343434',
                    }),
                  },
                  BarProgress: {
                    style: () => ({
                      backgroundColor: '#EDFF6D',
                    }),
                  },
                }}
              />
              {/* Tooltip trigger at progress point */}
              <div
                className='absolute top-1/2 -translate-y-1/2'
                style={{ left: `${progress}%` }}
              >
                <Tooltip
                  tootipContentClassName='bg-mono-100 rounded-lg py-1.5 px-2 '
                  tootipArrowClassName='fill-mono-100'
                  className='bg-transparent hover:border-transparent hover:shadow-transparent'
                  content={
                    <div className='font-Poppins bg-transparent text-xs font-normal text-tab-color'>
                      {t('YOU_NEED_SREP', {
                        count: nextTierDiff > 0 ? nextTierDiff : 0,
                      })}
                    </div>
                  }
                >
                  <div className='ml-2 h-1.5 w-1.5 cursor-pointer rounded-full bg-transparent' />
                </Tooltip>
              </div>
            </div>
          </div>
          <div className='mt-2 flex flex-col items-start'>
            <div className='flex flex-col items-start gap-x-4 xs:flex-row xs:items-center'>
              {nextTier && (
                <Header6 className='text-base text-white-100'>
                  {t('UPCOMING_TIER')}: {nextTier.name}
                </Header6>
              )}
              <Link
                className='flex items-center gap-x-2 text-sm font-normal tracking-[0.6px] text-customGray underline'
                href={
                  'https://docs.singularityfinance.ai/sfi-value-proposition/core-pillars-of-the-sfi-l2/ai-centric-dapp/singularity-rep-points'
                }
                target='_blank'
              >
                {t('MORE_INFO')}
                <Image
                  src={getStaticAssetUrl(
                    `/sfi/static/pv2/open-in-new-gray.svg`
                  )}
                  alt='More info'
                  width={15}
                  height={15}
                />
              </Link>
            </div>
            {nextTier && (
              <Typography className='mt-2 text-sm font-normal tracking-[0.4px] text-yellowDark'>
                {t('STAKE_MORE')}
              </Typography>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
